(() => {
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const $ = (root, sel) => root.querySelector(sel);

  function safeJson(text, fallback) {
    try { return JSON.parse(text); } catch { return fallback; }
  }

  function moneyFormatter(currency) {
    const lang = document.documentElement.lang || "en-US";
    try { return new Intl.NumberFormat(lang, { style: "currency", currency }); }
    catch { return new Intl.NumberFormat(lang, { style: "currency", currency: "USD" }); }
  }

  // For price-per-unit ONLY (3 decimals)
  function moneyFormatterFixed(currency, decimals) {
    const lang = document.documentElement.lang || "en-US";
    try {
      return new Intl.NumberFormat(lang, {
        style: "currency",
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    } catch {
      return new Intl.NumberFormat(lang, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }
  }

  function fmtMoney(fmt, cents) {
    const n = Number(cents || 0) / 100;
    return fmt.format(n);
  }

  function fmtMoneyDollars(fmt, dollars) {
    return fmt.format(Number(dollars || 0));
  }

  function variantIdFromHref(href) {
    try {
      if (!href) return "";
      const url = new URL(href, window.location.origin);
      return String(url.searchParams.get("variant") || "");
    } catch {
      return "";
    }
  }

  function withVariantInHref(href, variantId) {
    try {
      const url = new URL(String(href || ""), window.location.origin);
      if (variantId) url.searchParams.set("variant", String(variantId));
      return `${url.pathname}${url.search}`;
    } catch {
      return href || "";
    }
  }

  // Robust delegated gallery thumb -> main swap
  function initOfferGallery(root) {
    const galleries = Array.from(root.querySelectorAll("[data-offer-gallery], .offer-gallery"));
    if (!galleries.length) return;

    galleries.forEach((gallery) => {
      if (!gallery || gallery.__offerGalleryInit) return;
      gallery.__offerGalleryInit = true;

      function getThumbAlt(thumb) {
        const img = thumb?.querySelector("img");
        const alt = (img?.getAttribute("alt") || "").trim();
        if (alt) return alt;
        const aria = (thumb?.getAttribute("aria-label") || "").trim();
        return aria || "Product image";
      }

      function getMainImg() {
        const el =
          gallery.querySelector("[data-offer-main]") ||
          gallery.querySelector(".offer-gallery__main") ||
          gallery.querySelector(".offer-gallery__stage img");
        if (!el) return null;
        if (el.tagName === "PICTURE") return el.querySelector("img");
        if (el.tagName === "IMG") return el;
        return el.querySelector("img");
      }

      function setPictureSources(newSrc) {
        const pic = gallery.querySelector("[data-offer-main] picture, .offer-gallery__stage picture");
        if (!pic) return;
        pic.querySelectorAll("source").forEach((s) => { s.srcset = newSrc; });
      }

      // Load full-size gallery images only when a shopper selects them.
      gallery.addEventListener("click", (e) => {
        const thumb = e.target.closest("[data-offer-thumb], .offer-gallery__thumb");
        if (!thumb || !gallery.contains(thumb)) return;

        const newSrc =
          thumb.getAttribute("data-offer-src") ||
          thumb.getAttribute("data-full") ||
          thumb.getAttribute("data-src") ||
          thumb.getAttribute("href");

        if (!newSrc) return;

        e.preventDefault();

        const img = getMainImg();
        if (img) {
          img.src = newSrc;
          img.srcset = newSrc;
          img.removeAttribute("sizes");
          img.loading = "eager";
          const alt = getThumbAlt(thumb);
          if (alt) img.setAttribute("alt", alt);
        }
        setPictureSources(newSrc);

        gallery.querySelectorAll("[data-offer-thumb], .offer-gallery__thumb").forEach((t) => {
          t.classList.remove("is-active");
          t.setAttribute("aria-pressed", "false");
        });
        thumb.classList.add("is-active");
        thumb.setAttribute("aria-pressed", "true");
      });

      gallery.querySelectorAll("[data-offer-thumb], .offer-gallery__thumb").forEach((thumb) => {
        const img = thumb.querySelector("img");
        if (!img) return;
        const currentAlt = (img.getAttribute("alt") || "").trim();
        if (!currentAlt) img.setAttribute("alt", getThumbAlt(thumb));
      });
      const main = getMainImg();
      if (main && !(main.getAttribute("alt") || "").trim()) {
        const activeThumb =
          gallery.querySelector("[data-offer-thumb].is-active, .offer-gallery__thumb.is-active") ||
          gallery.querySelector("[data-offer-thumb], .offer-gallery__thumb");
        main.setAttribute("alt", getThumbAlt(activeThumb));
      }
    });
  }

  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

  /**
   * Shrink text until it fits in its own box (no ellipsis).
   * Uses binary search on font-size.
   */
  function fitTextToBox(el, minPx, maxPx) {
    if (!el || !el.isConnected) return;
    const boxW = el.clientWidth;
    if (!boxW || boxW < 10) return;

    el.style.fontSize = `${maxPx}px`;
    if (el.scrollWidth <= boxW) return;

    let lo = minPx;
    let hi = maxPx;

    for (let i = 0; i < 10; i++) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (el.scrollWidth > boxW) hi = mid;
      else lo = mid;
    }

    el.style.fontSize = `${lo}px`;
  }

  function parsePctFromText(str) {
    const m = String(str || "").match(/(\d+(?:\.\d+)?)\s*%/);
    if (!m) return null;
    return clamp(Math.round(parseFloat(m[1])), 0, 95);
  }

  function planPctFromAlloc(alloc, fallbackCompareAtCents) {
    if (!alloc) return null;

    const p = Number(alloc.price);
    const cRaw = Number(alloc.compareAt);

    if (!isFinite(p) || p <= 0) return null;

    const c = (isFinite(cRaw) && cRaw > 0) ? cRaw : Number(fallbackCompareAtCents);
    if (!isFinite(c) || c <= 0) return null;

    return clamp(Math.round((1 - (p / c)) * 100), 0, 95);
  }

  function replacePctInTemplate(template, pct) {
    const t = String(template || "").trim();
    if (!t) return `Save ${pct}%`;

    // If the template already contains a percent, replace just the number
    if (/%/.test(t) && /(\d+(?:\.\d+)?)\s*%/.test(t)) {
      return t.replace(/(\d+(?:\.\d+)?)\s*%/, `${pct}%`);
    }

    // Fallbacks
    if (/save/i.test(t)) return `Save ${pct}%`;
    return `${pct}% Off`;
  }
  function initOfferCTAv2(root) {

    initOfferGallery(root);

    const configEl = $(root, "[data-offer-v2-config]");
    const form = $(root, "[data-offer-v2-form]");

    if (!configEl || !form || root.__newPdpOfferCTAv2Init) return;


    const config = safeJson(configEl.textContent, null);
    if (!config) return;
    const configDefaults = (config.defaults && typeof config.defaults === "object") ? config.defaults : {};
    const defaultPurchaseOption =
      String(configDefaults.purchaseOption || "one_time").toLowerCase() === "subscribe"
        ? "subscribe"
        : "one_time";
    const defaultBagQtyRaw = configDefaults.qty;
    const hasNewPdpMarker = config.newPdpCta === true;
    const hasLegacyNewPdpShape =
      typeof config.productHandle === "string" &&
      config.productHandle.trim().length > 0 &&
      Array.isArray(config.variants);
    const isNewPdpConfig =
      hasNewPdpMarker ||
      hasLegacyNewPdpShape ||
      Array.isArray(config.flavors) ||
      Array.isArray(config.groupProductHandles) ||
      Object.prototype.hasOwnProperty.call(config, "flavorNavigationMode");
    if (!isNewPdpConfig) return;
    root.__newPdpOfferCTAv2Init = true;
    const flavorNavigationMode = String(config.flavorNavigationMode || "in_place").toLowerCase();
    const shouldNavigateOnFlavorClick = flavorNavigationMode === "navigate";

    const fmt = moneyFormatter(config.currency);
    const fmt3 = moneyFormatterFixed(config.currency, 3); // per-unit only
    const revliftLinesEl = form ? form.querySelector("[data-offer-v2-revlift-lines]") : null;
    const primaryVariantInput = form ? form.querySelector("[data-offer-v2-primary-variant]") : null;
    const primaryQuantityInput = form ? form.querySelector("[data-offer-v2-primary-quantity]") : null;

    let variants = Array.isArray(config.variants) ? config.variants : [];
    let flavors = Array.isArray(config.flavors) ? config.flavors : [];
    let variantsById = new Map();
    let inferredFlavors = [];
    let resolvedFlavors = [];
    let flavorsByHandle = new Map();

    function normalizeFlavorTitle(title, handle) {
      const raw = String(title || handle || "Flavor");
      return raw
        .replace(/Freeze Dried\s+/i, "")
        .replace(/Soft & Chewy\s+/i, "")
        .replace(/Crunchy\s+/i, "")
        .replace(/Jerky\s+/i, "")
        .replace(/\s+Training Treats$/i, "")
        .replace(/\s+Training$/i, "")
        .replace(/\s+Treats$/i, "")
        .replace(/\s+Dog$/i, "")
        .trim();
    }

    function isBundleSku(sku) {
      const s = String(sku || "").toLowerCase();
      return s.startsWith("bnd") || s.startsWith("bundlesku");
    }

    function isTruthyDiscontinued(raw) {
      const text = String(raw ?? "").trim().toLowerCase();
      return raw === true || raw === 1 || text === "true" || text === "1" || text === "yes" || text === "on";
    }

    function getDiscontinuedRaw(v) {
      return v?.discontinued ?? v?.metafields?.custom?.discontinued?.value ?? v?.metafields?.custom?.discontinued;
    }

    function isDiscontinued(v) {
      return isTruthyDiscontinued(getDiscontinuedRaw(v));
    }

    function bestVariantForHandle(handle) {
      const configuredFlavor = flavorsByHandle.get(String(handle));
      const configuredBaseId = String(configuredFlavor?.baseVariantId || "");
      const configuredBaseVariant = configuredBaseId ? variantsById.get(configuredBaseId) : null;
      if (configuredBaseVariant) return configuredBaseVariant;

      const nonBundle = variants
        .filter((v) => String(v?.handle || "") === String(handle))
        .filter((v) => !isBundleSku(v?.sku));
      if (!nonBundle.length) return null;

      const includeDiscontinuedForSmallest = config?.useSmallestVariantIncludingDiscontinued === true;
      const nonDiscontinued = nonBundle.filter((v) => !isDiscontinued(v));
      const pool = includeDiscontinuedForSmallest
        ? nonBundle
        : (nonDiscontinued.length ? nonDiscontinued : nonBundle);

      const availablePool = pool.filter((v) => !!v?.available);
      const pickFrom = availablePool.length ? availablePool : pool;

      const sortedAsc = pickFrom
        .slice()
        .sort((a, b) => {
          const aw = Number(a?.weight || 0);
          const bw = Number(b?.weight || 0);
          if (aw !== bw) return aw - bw;
          return Number(a?.price || 0) - Number(b?.price || 0);
        });
      return sortedAsc[0] || null;
    }

    function toHandle(value) {
      return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/['"]/g, "")
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    function recomputeFlavorState() {
      variantsById = new Map(variants.map((v) => [String(v.id), v]));
      inferredFlavors = (() => {
      const grouped = new Map();
      variants.forEach((v) => {
        if (!v) return;
        const handle = String(v.handle || "");
        if (!handle) return;
        if (!grouped.has(handle)) grouped.set(handle, []);
        grouped.get(handle).push(v);
      });

      const out = [];
      grouped.forEach((list, handle) => {
        const sorted = list
          .slice()
          .sort((a, b) => Number(a?.price || 0) - Number(b?.price || 0));
        const base = sorted[0];
        if (!base) return;
        out.push({
          handle,
          title: String(base.title || handle),
          baseVariantId: String(base.id || ""),
          twoBagVariantId: String(sorted[1]?.id || "")
        });
      });
      return out;
    })();

      resolvedFlavors = (flavors.length ? flavors : inferredFlavors)
        .filter((f) => f && f.handle && f.baseVariantId);

      flavorsByHandle = new Map(
        resolvedFlavors
          .filter((f) => f && f.handle)
          .map((f) => [String(f.handle), f])
      );
    }

    recomputeFlavorState();

    const ctaDebug = (() => {
      try {
        return new URLSearchParams(window.location.search).get("cta_debug") === "1";
      } catch {
        return false;
      }
    })();
    if (ctaDebug) {
      console.log("[new-pdp-cta-v2] debug", {
        productHandle: config.productHandle,
        flavorsLen: flavors.length,
        resolvedFlavorsLen: resolvedFlavors.length,
        variantsLen: variants.length,
        flavors,
        resolvedFlavors,
        variantsPreview: variants.slice(0, 8)
      });
    }
    const qtyUnitSingular = String(config.qtyUnitSingular || "treat").trim() || "treat";
    const qtyUnitPlural = String(config.qtyUnitPlural || `${qtyUnitSingular}s`).trim() || `${qtyUnitSingular}s`;
    const hasProductTreatTotals = config.hasProductTreatTotals === true;
    const treatsByQtyByHandle = (config.treatsByQtyByHandle && typeof config.treatsByQtyByHandle === "object")
      ? config.treatsByQtyByHandle
      : {};
    const hasProductTreatTotalsByHandle = (config.hasProductTreatTotalsByHandle && typeof config.hasProductTreatTotalsByHandle === "object")
      ? config.hasProductTreatTotalsByHandle
      : {};
    const treatsPerBag = Math.max(1, Number(config.treatsPerBag || 1));
    const treatsByQty = (config.treatsByQty && typeof config.treatsByQty === "object")
      ? config.treatsByQty
      : {};
    const planOptionsWrap = $(root, "[data-offer-v2-plan-options]");
    const planOptionsByHandle = new Map();
    const planFetchByHandle = new Map();
    const planAllocHydratedByHandle = new Set();
    const spAllocByVariantPlan = new Map();
    const preloadedPlansByHandle = (config.sellingPlansByHandle && typeof config.sellingPlansByHandle === "object")
      ? config.sellingPlansByHandle
      : {};
    let selectedPlanKey = null;
    let planDebugEl = null;

    const treatsTotalByQty = new Map([1, 2, 4].map((q) => {
      const explicit = Number(treatsByQty[String(q)]);
      const fallback = treatsPerBag * q;
      const total = Number.isFinite(explicit) && explicit > 0 ? explicit : fallback;
      return [q, Math.max(1, Math.round(total))];
    }));
    let spAllocById = new Map(
      variants.map(v => [
        String(v.id),
        (v && v.spPrice != null)
          ? { price: Number(v.spPrice), compareAt: Number(v.spCompareAt ?? v.price) }
          : null
      ])
    );

    variants.forEach((v) => {
      const planId = Number(v?.spId || 0);
      const planPrice = Number(v?.spPrice);
      if (!planId || !isFinite(planPrice) || planPrice <= 0) return;
      const compareAt = Number(v?.spCompareAt ?? v?.price ?? 0);
      spAllocByVariantPlan.set(`${String(v.id)}:${String(planId)}`, {
        price: planPrice,
        compareAt: isFinite(compareAt) && compareAt > 0 ? compareAt : Number(v?.price || 0),
      });
    });

    function intervalWeight(interval, count) {
      const unit = String(interval || '').toLowerCase();
      const n = Math.max(1, Number(count || 1));
      if (unit === 'day') return n;
      if (unit === 'week') return n * 7;
      if (unit === 'month') return n * 30;
      if (unit === 'year') return n * 365;
      return 999999;
    }

    function formatPlanLabel(interval, intervalCount) {
      const unit = String(interval || '').toLowerCase();
      const count = Math.max(1, Number(intervalCount || 1));
      const base = unit === 'day' ? 'Day' : unit === 'week' ? 'Week' : unit === 'month' ? 'Month' : unit === 'year' ? 'Year' : 'Delivery';
      return `${count} ${base}${count === 1 ? '' : 's'}`;
    }

    function cleanPlanLabel(label) {
      const raw = String(label || '').trim();
      if (!raw) return '';

      let out = raw
        .replace(/^deliver\s+every\s+/i, '')
        .replace(/^delivery\s+every\s+/i, '')
        .replace(/^ship\s+every\s+/i, '')
        .replace(/^ships?\s+every\s+/i, '')
        .trim();

      out = out
        .split(/\s+/)
        .map((token) => token ? token.charAt(0).toUpperCase() + token.slice(1).toLowerCase() : token)
        .join(' ');

      return out;
    }


    function normalizePlan(sp) {
      const id = Number(sp?.id || 0);
      if (!id) return null;

      const directInterval = String(sp?.interval || '').toLowerCase();
      const directCount = Number(sp?.intervalCount || sp?.interval_count || 0);
      const deliveryInterval = String(sp?.delivery_policy?.interval || '').toLowerCase();
      const deliveryCount = Number(sp?.delivery_policy?.interval_count || 0);
      const billingInterval = String(sp?.billing_policy?.interval || '').toLowerCase();
      const billingCount = Number(sp?.billing_policy?.interval_count || 0);

      let interval = directInterval || deliveryInterval || billingInterval;
      let intervalCount = Math.max(1, directCount || deliveryCount || billingCount || 1);

      const rawLabel = cleanPlanLabel(sp?.label || sp?.name || '');
      if (!interval) {
        const optionValue = String(sp?.options?.[0]?.value || sp?.options?.[0] || '').trim();
        const text = `${optionValue} ${rawLabel}`.toLowerCase();
        const match = text.match(/(\d+)\s*(day|week|month|year)s?\b/);
        if (match) {
          intervalCount = Math.max(1, Number(match[1] || 1));
          interval = String(match[2] || '').toLowerCase();
        }
      }

      if (!interval) {
        return {
          id,
          interval: 'custom',
          intervalCount: 1,
          key: `id:${id}`,
          label: rawLabel || `Plan ${id}`,
          sortWeight: 999999,
        };
      }

      return {
        id,
        interval,
        intervalCount,
        key: `${interval}:${intervalCount}:${id}`,
        label: rawLabel || cleanPlanLabel(formatPlanLabel(interval, intervalCount)),
        sortWeight: intervalWeight(interval, intervalCount),
      };
    }
    function allocKey(variantId, planId) {
      return `${String(variantId)}:${String(planId)}`;
    }

    function getPlanAlloc(variantId, planId) {
      if (!variantId || !planId) return null;
      return spAllocByVariantPlan.get(allocKey(variantId, planId)) || null;
    }

    function getAnyPlanAlloc(variantId) {
      const id = String(variantId || "");
      if (!id) return null;

      const direct = spAllocById.get(id);
      if (direct && Number(direct.price) > 0) return direct;

      for (const [key, alloc] of spAllocByVariantPlan.entries()) {
        if (!String(key).startsWith(`${id}:`)) continue;
        if (alloc && Number(alloc.price) > 0) return alloc;
      }

      return null;
    }

    function selectedSubscriptionState(ids = selectedVariantIds()) {
      if (!ids.length) return { available: false, planId: null, allocations: new Map(), eligiblePlans: [] };
      const uniqueIds = [...new Set(ids.map((id) => String(id)).filter(Boolean))];
      if (!uniqueIds.length) return { available: false, planId: null, allocations: new Map(), eligiblePlans: [] };

      const handle = selectedFlavorHandle();
      const hasKnownIneligibleVariant = uniqueIds.some((id) => {
        const variant = variantsById.get(String(id));
        return variant && variant.hasSellingPlanAllocations === false;
      });
      const isHydrated = planAllocHydratedByHandle.has(String(handle || ""));
      if (isHydrated && hasKnownIneligibleVariant) {
        return { available: false, planId: null, allocations: new Map(), eligiblePlans: [] };
      }

      const allocations = new Map();
      let planId = null;
      const plans = getPlansForHandle(handle);
      const eligiblePlans = plans.filter((plan) => {
        const id = Number(plan?.id || 0);
        return id > 0 && uniqueIds.every((variantId) => {
          const alloc = getPlanAlloc(variantId, id);
          return alloc && Number(alloc.price) > 0;
        });
      });

      if (eligiblePlans.length) {
        let selectedPlan = null;
        if (selectedPlanKey) {
          selectedPlan = eligiblePlans.find((plan) => plan.key === selectedPlanKey) || null;
        }
        if (!selectedPlan) {
          selectedPlan =
            eligiblePlans.find((plan) => plan.interval === "month" && Number(plan.intervalCount) === 1) ||
            eligiblePlans[0];
          selectedPlanKey = selectedPlan?.key || null;
        }

        planId = Number(selectedPlan?.id || 0) || null;
        if (planId) {
          uniqueIds.forEach((id) => {
            const alloc = getPlanAlloc(id, planId);
            if (alloc) allocations.set(String(id), alloc);
          });
          return { available: allocations.size === uniqueIds.length, planId, allocations, eligiblePlans };
        }
      }

      if (!isHydrated) {
        let selectedPlan = null;
        if (selectedPlanKey && plans.length) {
          selectedPlan = plans.find((plan) => plan.key === selectedPlanKey) || null;
        }
        if (!selectedPlan && plans.length) {
          selectedPlan =
            plans.find((plan) => plan.interval === "month" && Number(plan.intervalCount) === 1) ||
            plans[0];
          selectedPlanKey = selectedPlan?.key || null;
        }

        planId = Number(selectedPlan?.id || 0) || null;
        if (!planId) {
          planId = uniqueIds
            .map((id) => Number(variantsById.get(String(id))?.spId || 0))
            .find((id) => id > 0) || null;
        }
        uniqueIds.forEach((id) => {
          const variant = variantsById.get(String(id));
          const alloc =
            getPlanAlloc(id, planId) ||
            spAllocById.get(String(id)) ||
            (variant ? { price: Number(variant.price || 0), compareAt: Number(variant.price || 0) } : null);
          if (alloc) allocations.set(String(id), alloc);
        });

        return {
          available: Number(planId || 0) > 0 && allocations.size === uniqueIds.length,
          planId,
          allocations,
          eligiblePlans: plans,
        };
      }

      const handlePlanId = getSelectedPlanIdForHandle(handle);
      if (handlePlanId) {
        const allHaveSelectedPlan = uniqueIds.every((id) => {
          const alloc = getPlanAlloc(id, handlePlanId);
          if (alloc && Number(alloc.price) > 0) {
            allocations.set(String(id), alloc);
            return true;
          }
          return false;
        });

        if (allHaveSelectedPlan) {
          return { available: true, planId: handlePlanId, allocations, eligiblePlans };
        }
      }

      const candidatePlanIds = [];
      uniqueIds.forEach((id) => {
        const variant = variantsById.get(String(id));
        const variantPlanId = Number(variant?.spId || 0);
        if (variantPlanId > 0 && !candidatePlanIds.includes(variantPlanId)) {
          candidatePlanIds.push(variantPlanId);
        }
      });

      for (const candidateId of candidatePlanIds) {
        const candidateAllocations = new Map();
        const allHavePlan = uniqueIds.every((id) => {
          const alloc = getPlanAlloc(id, candidateId);
          if (alloc && Number(alloc.price) > 0) {
            candidateAllocations.set(String(id), alloc);
            return true;
          }
          return false;
        });

        if (allHavePlan) {
          planId = candidateId;
          return { available: true, planId, allocations: candidateAllocations, eligiblePlans };
        }
      }

      const allHaveAnyPlan = uniqueIds.every((id) => {
        const alloc = getAnyPlanAlloc(id);
        if (alloc) {
          allocations.set(String(id), alloc);
          return true;
        }
        return false;
      });

      return {
        available: allHaveAnyPlan && Number(handlePlanId || 0) > 0,
        planId: allHaveAnyPlan ? handlePlanId || null : null,
        allocations: allHaveAnyPlan ? allocations : new Map(),
        eligiblePlans,
      };
    }

    function setSubscribeOptionAvailable(isAvailable) {
      poCards.forEach((card) => {
        const isSubscribe = card.getAttribute("data-offer-v2-po") === "subscribe";
        if (!isSubscribe) return;
        card.hidden = !isAvailable;
        card.setAttribute("aria-hidden", isAvailable ? "false" : "true");
        if (isAvailable) {
          card.style.removeProperty("display");
        } else {
          card.style.setProperty("display", "none", "important");
          card.classList.remove("is-active");
        }
      });

      poRadios.forEach((radio) => {
        if (radio.value !== "subscribe") return;
        radio.disabled = !isAvailable;
        if (!isAvailable) radio.checked = false;
      });
    }

    function getPlansForHandle(handle) {
      const key = String(handle || '').trim();
      if (!key) return [];
      return planOptionsByHandle.get(key) || [];
    }

    Object.keys(preloadedPlansByHandle).forEach((handle) => {
      const key = String(handle || '').trim();
      if (!key || planOptionsByHandle.has(key)) return;

      const rawPlans = Array.isArray(preloadedPlansByHandle[handle])
        ? preloadedPlansByHandle[handle]
        : [];

      const byKey = new Map();
      rawPlans.forEach((rawPlan) => {
        const plan = normalizePlan(rawPlan);
        if (!plan || !plan.id || !plan.key) return;
        if (!byKey.has(plan.key)) byKey.set(plan.key, plan);
      });


      const plans = Array.from(byKey.values()).sort((a, b) => {
        const aw = Number(a.sortWeight ?? intervalWeight(a.interval, a.intervalCount));
        const bw = Number(b.sortWeight ?? intervalWeight(b.interval, b.intervalCount));
        if (aw !== bw) return aw - bw;

        const ac = Number(a.intervalCount || 0);
        const bc = Number(b.intervalCount || 0);
        if (ac !== bc) return ac - bc;

        return Number(a.id || 0) - Number(b.id || 0);
      });

      if (plans.length) {
        planOptionsByHandle.set(key, plans);
      }
    });

    function ensurePlanDebugEl() {
      if (!ctaDebug) return null;
      if (planDebugEl && planDebugEl.isConnected) return planDebugEl;

      const el = document.createElement('pre');
      el.setAttribute('data-offer-v2-plan-debug', '');
      el.style.margin = '8px 0 0';
      el.style.padding = '8px';
      el.style.fontSize = '12px';
      el.style.lineHeight = '1.4';
      el.style.whiteSpace = 'pre-wrap';
      el.style.borderRadius = '8px';
      el.style.border = '1px dashed #7a4d4f';
      el.style.background = '#fff7f3';
      el.style.color = '#552628';

      const mount = planOptionsWrap?.parentElement || root;
      mount.appendChild(el);
      planDebugEl = el;
      return planDebugEl;
    }

    function renderPlanDebug(state) {
      if (!ctaDebug) return;
      const el = ensurePlanDebugEl();
      if (!el) return;

      const handle = String(state?.activeHandle || '');
      const preloadedRaw = handle && Array.isArray(preloadedPlansByHandle[handle])
        ? preloadedPlansByHandle[handle].length
        : 0;

      const lines = [
        'CTA Debug (plan options)',
        'handle=' + (handle || '(none)'),
        'purchaseOption=' + purchaseOption,
        'plansInMap=' + Number(state?.plansCount || 0),
        'preloadedRaw=' + preloadedRaw,
        'isFetching=' + (state?.isFetching ? 'true' : 'false'),
        'shouldShow=' + (state?.shouldShow ? 'true' : 'false'),
        'hasWrap=' + (state?.hasWrap ? 'true' : 'false')
      ];
      el.textContent = lines.join('\n');
    }
    function ensureSelectedPlanKeyForHandle(handle) {
      const plans = getPlansForHandle(handle);
      if (!plans.length) {
        selectedPlanKey = null;
        return null;
      }

      if (selectedPlanKey && plans.some((p) => p.key === selectedPlanKey)) {
        return selectedPlanKey;
      }

      const monthly = plans.find((p) => p.interval === 'month' && Number(p.intervalCount) === 1);
      selectedPlanKey = (monthly || plans[0]).key;
      return selectedPlanKey;
    }

    function getSelectedPlanIdForHandle(handle) {
      const plans = getPlansForHandle(handle);
      if (!plans.length) return null;
      const key = ensureSelectedPlanKeyForHandle(handle);
      const selected = plans.find((p) => p.key === key) || plans[0];
      return Number(selected?.id || 0) || null;
    }

    async function loadPlansForHandle(handle) {
      const key = String(handle || '').trim();
      if (!key) return [];
      const existingPlans = planOptionsByHandle.get(key) || [];
      if (existingPlans.length && planAllocHydratedByHandle.has(key)) return existingPlans;

      try {
        const res = await fetch(`/products/${encodeURIComponent(key)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          planOptionsByHandle.set(key, []);
          planAllocHydratedByHandle.add(key);
          return [];
        }

        const prod = await res.json();

        (prod.variants || []).forEach((pv) => {
          const variantId = Number(pv?.id || 0);
          const variant = variantsById.get(String(variantId));
          const allocations = Array.isArray(pv?.selling_plan_allocations) ? pv.selling_plan_allocations : [];
          if (variant) variant.hasSellingPlanAllocations = allocations.length > 0;

          allocations.forEach((alloc) => {
            const planId = Number(alloc?.selling_plan_id || 0);
            const price = Number(alloc?.price);
            if (!planId || !variantId || !isFinite(price) || price <= 0) return;
            const compareAt = Number(alloc?.compare_at_price ?? pv?.price ?? 0);
            spAllocByVariantPlan.set(allocKey(variantId, planId), {
              price,
              compareAt: isFinite(compareAt) && compareAt > 0 ? compareAt : Number(pv?.price || 0),
            });
          });
        });

        const byKey = new Map();
        (prod.selling_plan_groups || []).forEach((group) => {
          if (String(group?.app_id || "").trim().toLowerCase() === "bundlesubscriptions") return;
          (group?.selling_plans || []).forEach((sp) => {
            const plan = normalizePlan(sp);
            if (!plan || !plan.id || !plan.key) return;
            if (!byKey.has(plan.key)) byKey.set(plan.key, plan);
          });
        });


        const plans = Array.from(byKey.values()).sort((a, b) => {
          const aw = Number(a.sortWeight ?? intervalWeight(a.interval, a.intervalCount));
          const bw = Number(b.sortWeight ?? intervalWeight(b.interval, b.intervalCount));
          if (aw !== bw) return aw - bw;

          const ac = Number(a.intervalCount || 0);
          const bc = Number(b.intervalCount || 0);
          if (ac !== bc) return ac - bc;

          return Number(a.id || 0) - Number(b.id || 0);
        });

        if (plans.length) {
          planOptionsByHandle.set(key, plans);
        } else if (existingPlans.length) {
          planOptionsByHandle.set(key, existingPlans);
        }
        planAllocHydratedByHandle.add(key);
        return plans.length ? plans : existingPlans;
      } catch (e) {
        console.warn('[new-pdp-cta-v2] failed loading selling plans', e);
        if (!existingPlans.length) planOptionsByHandle.set(key, []);
        return existingPlans;
      }
    }

    function maybeLoadPlansForHandle(handle) {
      const key = String(handle || '').trim();
      if (!key || planFetchByHandle.has(key)) return;
      const existingPlans = planOptionsByHandle.get(key) || [];
      if (existingPlans.length && planAllocHydratedByHandle.has(key)) return;

      const promise = loadPlansForHandle(key)
        .then(() => {
          renderPlanOptions();
          rerenderAll();
        })
        .finally(() => {
          planFetchByHandle.delete(key);
        });

      planFetchByHandle.set(key, promise);
    }

    function renderPlanOptions() {
      if (!planOptionsWrap) return;

      const activeHandle = selectedFlavorHandle();
      const subscriptionState = selectedSubscriptionState();
      const plans = subscriptionState.eligiblePlans && subscriptionState.eligiblePlans.length
        ? subscriptionState.eligiblePlans
        : getPlansForHandle(activeHandle);
      const shouldShow = purchaseOption === 'subscribe' && subscriptionState.available && plans.length > 0;

      planOptionsWrap.hidden = !shouldShow;
      if (!shouldShow) {
        planOptionsWrap.innerHTML = '';
        renderPlanDebug({
          activeHandle,
          plansCount: plans.length,
          shouldShow,
          hasWrap: !!planOptionsWrap,
          isFetching: planFetchByHandle.has(String(activeHandle || ''))
        });
        return;
      }

      ensureSelectedPlanKeyForHandle(activeHandle);

      planOptionsWrap.innerHTML = '';
      plans.forEach((plan) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'offer-cta-v2__poFreqBtn';
        btn.setAttribute('data-offer-v2-plan-key', plan.key);
        btn.setAttribute('aria-pressed', plan.key === selectedPlanKey ? 'true' : 'false');
        if (plan.key === selectedPlanKey) btn.classList.add('is-active');
        btn.textContent = plan.label;
        planOptionsWrap.appendChild(btn);
      });

      renderPlanDebug({
        activeHandle,
        plansCount: plans.length,
        shouldShow,
        hasWrap: !!planOptionsWrap,
        isFetching: planFetchByHandle.has(String(activeHandle || ''))
      });
    }
    async function bootstrapFromRuntimeIfEmpty() {
      if (variants.length > 0 && resolvedFlavors.length > 0) return;
      const productHandle = String(config.productHandle || "").trim();
      if (!productHandle) return;

      async function fetchProductJson(handle) {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) return null;
        return res.json();
      }

      try {
        const current = await fetchProductJson(productHandle);
        if (!current) return;

        let groupHandle = toHandle(config.groupHandle || "");
        if (!groupHandle) {
          const groupTag = (current.tags || []).find((t) => String(t).startsWith("group:"));
          if (groupTag) groupHandle = toHandle(String(groupTag).replace("group:", ""));
        }

        let handles = Array.isArray(config.groupProductHandles)
          ? config.groupProductHandles.map((h) => String(h || "").trim()).filter(Boolean)
          : [];
        if (!handles.length) handles = [productHandle];

        if (groupHandle && handles.length <= 1) {
          try {
            const cRes = await fetch(`/collections/${encodeURIComponent(groupHandle)}/products.json?limit=250`, {
              headers: { Accept: "application/json" },
            });
            if (cRes.ok) {
              const cJson = await cRes.json();
              const fromCollection = Array.isArray(cJson?.products)
                ? cJson.products.map((p) => String(p.handle || "").trim()).filter(Boolean)
                : [];
              if (fromCollection.length) handles = [...new Set(fromCollection)];
            }
          } catch (_) {}
        }
        handles = [...new Set(handles)];

        const products = (
          await Promise.all(handles.map((h) => fetchProductJson(h)))
        ).filter(Boolean);

        if (!products.length) return;

        const builtVariants = [];
        const builtFlavors = [];

        products.forEach((prod) => {
          const handle = String(prod.handle || "").trim();
          if (!handle) return;

          const variantsList = Array.isArray(prod.variants) ? prod.variants : [];

          let prodSpId = "";
          if (Array.isArray(prod.selling_plan_groups) && prod.selling_plan_groups.length) {
            for (const g of prod.selling_plan_groups) {
              if (String(g?.app_id || "").trim().toLowerCase() === "bundlesubscriptions") continue;
              const plans = Array.isArray(g?.selling_plans) ? g.selling_plans : [];
              const monthly = plans.find((sp) =>
                (sp?.delivery_policy?.interval === "month" && Number(sp?.delivery_policy?.interval_count) === 1) ||
                (sp?.billing_policy?.interval === "month" && Number(sp?.billing_policy?.interval_count) === 1)
              );
              if (monthly?.id) {
                prodSpId = String(monthly.id);
                break;
              }
              if (!prodSpId && plans[0]?.id) prodSpId = String(plans[0].id);
            }
          }

          const activeCandidates = variantsList
            .slice()
            .filter((v) => !isBundleSku(v?.sku) && !isDiscontinued(v))
            .sort((a, b) => Number(a?.price || 0) - Number(b?.price || 0));
          const nonBundleCandidates = variantsList
            .slice()
            .filter((v) => !isBundleSku(v?.sku))
            .sort((a, b) => Number(a?.price || 0) - Number(b?.price || 0));
          const sortedCandidates = activeCandidates.length ? activeCandidates : nonBundleCandidates;
          const base = sortedCandidates[0] || variantsList[0];
          const twoBag = sortedCandidates[1] || null;

          if (base?.id) {
            builtFlavors.push({
              handle,
              title: normalizeFlavorTitle(prod.title, handle),
              baseVariantId: String(base.id),
              twoBagVariantId: twoBag?.id ? String(twoBag.id) : "",
            });
          }

          variantsList.forEach((v) => {
            const vid = String(v?.id || "");
            if (!vid) return;
            const alloc = Array.isArray(v?.selling_plan_allocations)
              ? v.selling_plan_allocations.find((a) => String(a?.selling_plan_id || "") === String(prodSpId))
              : null;
            const hasSellingPlanAllocations = Array.isArray(v?.selling_plan_allocations) && v.selling_plan_allocations.length > 0;
            builtVariants.push({
              id: Number(v.id),
              title: String(prod.title || handle),
              handle,
              product_id: Number(prod.id || 0),
              price: Number(v.price || 0),
              compareAt: v.compare_at_price != null ? Number(v.compare_at_price) : null,
              available: !!v.available,
              spId: prodSpId,
              spPrice: alloc?.price != null ? Number(alloc.price) : null,
              spCompareAt: alloc?.compare_at_price != null ? Number(alloc.compare_at_price) : Number(v.price || 0),
              hasSellingPlanAllocations,
              sku: String(v.sku || ""),
              discontinued: isDiscontinued(v),
            });
          });
        });

        const dedupById = new Map();
        builtVariants.forEach((v) => dedupById.set(String(v.id), v));
        variants = Array.from(dedupById.values());
        flavors = builtFlavors;

        recomputeFlavorState();
        spAllocById = new Map(
          variants.map((v) => [
            String(v.id),
            v && v.spPrice != null ? { price: Number(v.spPrice), compareAt: Number(v.spCompareAt ?? v.price) } : null,
          ])
        );

                variants.forEach((v) => {
          const planId = Number(v?.spId || 0);
          const planPrice = Number(v?.spPrice);
          if (!planId || !isFinite(planPrice) || planPrice <= 0) return;
          const compareAt = Number(v?.spCompareAt ?? v?.price ?? 0);
          spAllocByVariantPlan.set(`${String(v.id)}:${String(planId)}`, {
            price: planPrice,
            compareAt: isFinite(compareAt) && compareAt > 0 ? compareAt : Number(v?.price || 0),
          });
        });
        if (ctaDebug) {
          console.log("[new-pdp-cta-v2] bootstrapped runtime data", {
            groupHandle,
            handles,
            flavorsLen: flavors.length,
            variantsLen: variants.length,
          });
        }
      } catch (e) {
        console.warn("[new-pdp-cta-v2] runtime bootstrap failed", e);
      }
    }

    const discounts = config.discounts || {};

    const sellingPlanIdRaw = (discounts.subscribeSellingPlanId || "").toString().trim();
    const sellingPlanId = (sellingPlanIdRaw && /^\d+$/.test(sellingPlanIdRaw)) ? Number(sellingPlanIdRaw) : null;

    const perkIcons = config.perkIcons || {};
    const includedIconUrl = (perkIcons.included || "").toString().trim();
    const excludedIconUrl = (perkIcons.excluded || "").toString().trim();

    const atcBtn = $(root, "[data-offer-v2-atc]");
    const titleEl = $(root, ".offer-cta-v2__title");
    const ratingsReviewsEl = $(root, ".offer-cta-v2__ratingsReviews");
    const ratingsNumEl = ratingsReviewsEl ? ratingsReviewsEl.querySelector(".offer-cta-v2__ratingsNum") : null;
    const titleSuffix = String(config.titleSuffix || "").trim();
    const productTitleByHandle = (config.productTitleByHandle && typeof config.productTitleByHandle === "object")
      ? config.productTitleByHandle
      : {};
    const titleSuffixByHandle = (config.titleSuffixByHandle && typeof config.titleSuffixByHandle === "object")
      ? config.titleSuffixByHandle
      : {};
    const reviewCountByHandle = (config.reviewCountByHandle && typeof config.reviewCountByHandle === "object")
      ? config.reviewCountByHandle
      : {};
    const defaultTitleText = titleEl ? String(titleEl.textContent || "").trim() : "";
    const defaultRatingsReviewsText = ratingsReviewsEl ? String(ratingsReviewsEl.textContent || "").trim() : "";
    const qtyCards = $$(root, "[data-offer-v2-qty]");
    const qtyCardsWrap = $(root, ".offer-cta-v2__qtyCards");
    const deliveryEl = $(root, "[data-offer-v2-delivery]");
    const deliveryDateEl = $(root, "[data-offer-v2-delivery-date]");

    const oneBadgeEl = $(root, '[data-offer-v2-po-badge="one_time"]');
    const subBadgeEl = $(root, '[data-offer-v2-po-badge="subscribe"]');

    const oneBadgeTemplate = oneBadgeEl ? oneBadgeEl.textContent.trim() : "";
    const subBadgeTemplate = subBadgeEl ? subBadgeEl.textContent.trim() : "";

    const stack = $(root, "[data-offer-v2-flavor-stack]");
    const tpl = $(root, "template[data-offer-v2-flavor-template]");
    const helpEl = $(root, "[data-offer-v2-help]");

    const poCards = $$(root, "[data-offer-v2-po]");
    const poRadios = $$(root, ".offer-cta-v2__poRadio");
    const fallbackPurchaseOptionsEl = $(root, "[data-offer-v2-fallback-purchase-options]");
    const academySingleQuantity = config?.academySingleQuantity === true;
    let reviewSyncRaf = 0;
    let reviewSyncAttempts = 0;


    let bagQty = 1;
    const validBagQty = new Set([1, 2, 4]);

    function normalizeBagQty(raw) {
      const n = Number(raw || 1);
      // Backward compatibility: legacy markup used 3 for the third card.
      if (n === 3) return 4;
      return n;
    }

    function positiveIntOrZero(raw) {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return 0;
      return Math.max(1, Math.round(n));
    }

    function getTreatTotalsForHandle(handle) {
      const selectedHandle = String(handle || "").trim();
      const handleTreats =
        selectedHandle && Object.prototype.hasOwnProperty.call(treatsByQtyByHandle, selectedHandle)
          ? treatsByQtyByHandle[selectedHandle]
          : {};

      const handleQty1 = positiveIntOrZero(handleTreats?.["1"]);
      const handleQty2 = positiveIntOrZero(handleTreats?.["2"]);
      const handleQty4 = positiveIntOrZero(handleTreats?.["4"]);

      const hasTreatsFlagForHandle =
        selectedHandle && Object.prototype.hasOwnProperty.call(hasProductTreatTotalsByHandle, selectedHandle)
          ? hasProductTreatTotalsByHandle[selectedHandle] === true
          : null;

      const defaultBaseTreats =
        positiveIntOrZero(treatsTotalByQty.get(1)) || Math.max(1, positiveIntOrZero(treatsPerBag) || 1);

      const derivedBaseTreats = handleQty1 > 0
        ? handleQty1
        : (handleQty2 > 0
          ? Math.max(1, Math.round(handleQty2 / 2))
          : (handleQty4 > 0
            ? Math.max(1, Math.round(handleQty4 / 4))
            : defaultBaseTreats));

      const totals = new Map([
        [1, handleQty1 > 0 ? handleQty1 : derivedBaseTreats],
        [2, handleQty2 > 0 ? handleQty2 : derivedBaseTreats * 2],
        [4, handleQty4 > 0 ? handleQty4 : derivedBaseTreats * 4],
      ]);

      const hasTreatTotalsForHandle =
        hasTreatsFlagForHandle != null
          ? hasTreatsFlagForHandle
          : ((handleQty1 > 0 || handleQty2 > 0 || handleQty4 > 0) || hasProductTreatTotals);

      return { totals, hasTreatTotalsForHandle };
    }

    let purchaseOption = defaultPurchaseOption;
    let userSelectedPurchaseOption = false;

    function placeDeliveryBeforeAtc() {
      if (!deliveryEl || !atcBtn || !atcBtn.parentNode) return;
      if (deliveryEl.nextElementSibling === atcBtn) return;

      atcBtn.parentNode.insertBefore(deliveryEl, atcBtn);
    }

    function isPillDisabled(el) {
      if (!el) return true;
      return el.classList.contains("is-disabled") || el.getAttribute("aria-disabled") === "true" || el.disabled;
    }

    function parsePctValue(raw) {
      if (raw == null || raw === "") return null;
      const n = Number(raw);
      if (isFinite(n)) {
        // Support either whole percent values (e.g. 20) or decimal ratios (e.g. 0.2)
        const pct = n > 0 && n < 1 ? n * 100 : n;
        return clamp(Math.round(pct), 0, 95);
      }
      const fromText = parsePctFromText(String(raw));
      return fromText == null ? null : fromText;
    }

    function selectedFlavorDiscounts() {
      const handle = selectedFlavorHandle();
      const flavor = handle ? (flavorsByHandle.get(String(handle)) || null) : null;

      const cfgOne = parsePctValue(discounts.oneQtyDisc);
      const cfgTwo = parsePctValue(discounts.twoQtyDisc);
      const cfgFour = parsePctValue(discounts.fourQtyDisc);
      const preferExperimentDiscounts = config?.useExperimentDiscounts === true;

      const flavorOne = preferExperimentDiscounts
        ? parsePctValue(flavor?.experimentOneQtyDisc)
        : parsePctValue(flavor?.oneQtyDisc);
      const flavorTwo = preferExperimentDiscounts
        ? parsePctValue(flavor?.experimentTwoQtyDisc)
        : parsePctValue(flavor?.twoQtyDisc);
      const flavorFour = preferExperimentDiscounts
        ? parsePctValue(flavor?.experimentFourQtyDisc)
        : parsePctValue(flavor?.fourQtyDisc);

      return {
        one: flavorOne != null ? flavorOne : (cfgOne != null ? cfgOne : 0),
        two: flavorTwo != null ? flavorTwo : (cfgTwo != null ? cfgTwo : 0),
        four: flavorFour != null ? flavorFour : (cfgFour != null ? cfgFour : 0),
      };
    }

    function qtySavePctFor(q) {
      const n = normalizeBagQty(q);
      if (n === 1) return selectedFlavorDiscounts().one;
      if (n === 2) return selectedFlavorDiscounts().two;
      if (n === 4) return selectedFlavorDiscounts().four;
      return 0;
    }

    function effectiveQtySavePctFor(q, purchaseOptionMode = "one_time") {
      const localPct = qtySavePctFor(q);
      return window.RevLiftPricing?.bestDiscountPercent
        ? window.RevLiftPricing.bestDiscountPercent(localPct, {
            purchaseOptionMode,
            variantId: selectedVariantIds()[0],
            productHandle: selectedFlavorHandle(),
            subtotalCents: Number(variantsById.get(String(selectedVariantIds()[0]))?.price || 0) * normalizeBagQty(q),
            quantity: normalizeBagQty(q)
          })
        : localPct;
    }

    function renderQtyCardBadges() {
      qtyCards.forEach((btn) => {
        const q = normalizeBagQty(btn.getAttribute("data-offer-v2-qty") || 1);
        const pct = effectiveQtySavePctFor(q, "one_time");
        let badge = btn.querySelector(".offer-cta-v2__qtyBadge");

        if (!badge && pct > 0) {
          badge = document.createElement("span");
          badge.className = "offer-cta-v2__qtyBadge";
          btn.prepend(badge);
        }

        if (badge) {
          if (pct > 0) {
            badge.hidden = false;
            badge.textContent = `Save ${pct}%`;
          } else if (q === 1) {
            badge.hidden = true;
          } else {
            badge.hidden = !badge.textContent.trim();
          }
        }
      });
    }

    async function hydrateSellingPlanAllocationsIfMissing() {
      const ids = selectedVariantIds();
      if (!ids.length) return;

      const sid =
        getSelectedPlanIdForHandle(selectedFlavorHandle()) ||
        ids
          .map((id) => Number(variantsById.get(String(id))?.spId || 0))
          .find((n) => n > 0) || null;
      if (!sid) return;

      const variantsNeedingData = ids
        .map((id) => variantsById.get(String(id)))
        .filter((v) => v && spAllocById.get(String(v.id)) == null);
      if (!variantsNeedingData.length) return;

      const handles = [...new Set(variantsNeedingData.map((v) => v.handle).filter(Boolean))];

      try {
        for (const handle of handles) {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: "application/json" },
          });
          if (!res.ok) continue;

          const prod = await res.json();

          (prod.variants || []).forEach((pv) => {
            const vid = String(pv.id);
            const alloc = (pv.selling_plan_allocations || []).find(
              (x) => Number(x.selling_plan_id) === Number(sid)
            );

            if (!alloc) return;

            const price = Number(alloc.price);
            const compareAt = Number(alloc.compare_at_price ?? pv.price ?? 0);

            if (isFinite(price) && price > 0) {
              spAllocById.set(vid, {
                price,
                compareAt: isFinite(compareAt) && compareAt > 0 ? compareAt : Number(pv.price || 0),
              });
            }
          });
        }

        rerenderAll();
      } catch (e) {
        console.warn("Failed to hydrate selling plan allocations", e);
      }
    }


    function perkIncluded(perk, q) {
      const key = `q${q}`;
      return !!(config?.perks?.[perk]?.[key]);
    }

    function setMarkIcon(markEl, included) {
      if (!markEl) return;
      const url = included ? includedIconUrl : excludedIconUrl;

      if (url) {
        markEl.classList.add("has-img");
        const existing = markEl.querySelector("img");
        if (!existing || existing.getAttribute("src") !== url) {
          markEl.innerHTML = `<img src="${url}" alt="" loading="lazy" />`;
        }
      } else {
        markEl.classList.remove("has-img");
        markEl.innerHTML = "";
      }
    }

    function renderQtyCardPerks() {
      const perkEls = $$(root, "[data-offer-v2-perk]");
      perkEls.forEach((el) => {
        const perk = el.getAttribute("data-offer-v2-perk");
        const q = Number(el.getAttribute("data-offer-v2-perk-q") || 1);
        const included = perkIncluded(perk, q);

        el.classList.toggle("is-included", included);
        el.classList.toggle("is-excluded", !included);

        setMarkIcon(el.querySelector(".offer-cta-v2__qtyMark"), included);
      });
    }

    function fitQtyPerkText() {
      const texts = $$(root, ".offer-cta-v2__qtyPerkText:not([data-no-fit])");
      const styles = getComputedStyle(root);
      const maxPx = parseFloat(styles.getPropertyValue("--perk-font-max")) || 12;
      const minPx = parseFloat(styles.getPropertyValue("--perk-font-min")) || 9;

      texts.forEach((t) => { t.style.fontSize = ""; });

      requestAnimationFrame(() => {
        texts.forEach((t) => fitTextToBox(t, minPx, maxPx));
      });
    }

    function renderQtyCardTreatCounts() {
      const handle = selectedFlavorHandle();
      const { totals, hasTreatTotalsForHandle } = getTreatTotalsForHandle(handle);

      qtyCards.forEach((btn) => {
        const q = normalizeBagQty(btn.getAttribute("data-offer-v2-qty") || 1);
        const metaEl = btn.querySelector(".offer-cta-v2__qtyMeta");
        if (!metaEl) return;

        if (!hasTreatTotalsForHandle) {
          metaEl.hidden = true;
          return;
        }

        const total =
          totals.get(q) ||
          treatsTotalByQty.get(q) ||
          Math.max(1, positiveIntOrZero(treatsPerBag) * Math.max(1, q));
        metaEl.hidden = false;
        const nextText = `Appr. ${Number(total).toLocaleString()} ${qtyUnitPlural}`;
        if (metaEl.textContent !== nextText) metaEl.textContent = nextText;
      });
    }

    function handleFromCurrentUrl(url) {
      try {
        const path = String(url?.pathname || "");
        const match = path.match(/\/products\/([^/?#]+)/i);
        return match?.[1] ? decodeURIComponent(match[1]) : "";
      } catch {
        return "";
      }
    }

    function syncGalleryToHandle(handle) {
      const h = String(handle || "").trim();
      if (!h) return;
      const galleriesWrap = root.querySelector("[data-offer-v2-galleries]");
      if (!galleriesWrap) return;

      const slots = galleriesWrap.querySelectorAll("[data-offer-v2-gallery]");
      slots.forEach((slot) => {
        const isMatch = String(slot.getAttribute("data-offer-v2-handle") || "") === h;
        slot.toggleAttribute("hidden", !isMatch);
        slot.classList.toggle("is-active", isMatch);
      });
    }

    function ensureRows() {
      if (!stack) return;
      if (stack.dataset.offerV2RowsInit === "1") return;

      // Remove any previously rendered rows before rebuilding.
      $$(stack, "[data-offer-v2-bag-row], [data-offer-v2-pill-row]").forEach((node) => {
        if (!node.closest("template")) node.remove();
      });

      const frag = tpl ? tpl.content.cloneNode(true) : document.createDocumentFragment();

      let bagRow = frag.querySelector("[data-offer-v2-bag-row]");
      let pillRow = bagRow ? bagRow.querySelector("[data-offer-v2-pill-row]") : null;

      if (!bagRow) {
        bagRow = document.createElement("div");
        bagRow.className = "offer-cta-v2__bagRow";
        bagRow.setAttribute("data-offer-v2-bag-row", "");

        const label = document.createElement("span");
        label.className = "offer-cta-v2__bagText";
        label.setAttribute("data-offer-v2-bag-label", "");
        label.textContent = "Choose Flavor";
        bagRow.appendChild(label);
      }

      if (!pillRow) {
        pillRow = document.createElement("div");
        pillRow.className = "offer-cta-v2__boxRow";
        pillRow.setAttribute("data-offer-v2-pill-row", "");
        bagRow.appendChild(pillRow);
      }

      if (!bagRow.parentNode) {
        frag.appendChild(bagRow);
      }

      const existingTemplatePills = pillRow
        ? Array.from(pillRow.querySelectorAll("[data-offer-v2-pill]"))
        : [];

      // Build pills from resolved flavors only when template pills are absent.
      if (pillRow && resolvedFlavors.length && existingTemplatePills.length === 0) {
        pillRow.innerHTML = "";
        resolvedFlavors.forEach((flavor) => {
          const handle = String(flavor?.handle || "");
          const title = String(flavor?.title || handle || "Flavor");
          const baseId = String(flavor?.baseVariantId || "");
          const isLimitedEdition = flavor?.limitedEdition === true;
          if (!handle || !baseId) return;

          const baseVariant = variantsById.get(baseId);
          const isAvailable = !!(baseVariant && baseVariant.available);

          const a = document.createElement("a");
          a.className = `offer-cta-v2__box${isAvailable ? "" : " is-disabled"}${isLimitedEdition ? " has-limited-edition-badge" : ""}`;
          a.setAttribute("data-offer-v2-pill", "");
          a.setAttribute("data-offer-v2-variant-id", baseId);
          a.setAttribute("data-offer-v2-handle", handle);
          a.setAttribute("href", `/products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(baseId)}`);
          if (!isAvailable) {
            a.setAttribute("aria-disabled", "true");
            a.setAttribute("tabindex", "-1");
          }
          const label = document.createElement("span");
          label.className = "offer-cta-v2__boxLabel";
          label.textContent = title;
          a.appendChild(label);
          if (isLimitedEdition) {
            const badge = document.createElement("span");
            badge.className = "offer-cta-v2__limitedBadge";
            badge.textContent = "Limited Edition";
            a.appendChild(badge);
          }
          pillRow.appendChild(a);
        });
      }

      const boxes = Array.from(frag.querySelectorAll("[data-offer-v2-pill]"));
      boxes.forEach((b) => {
        const handle = String(b.getAttribute("data-offer-v2-handle") || "");
        if (!handle) return;
        const explicitId = String(b.getAttribute("data-offer-v2-variant-id") || "");
        const explicitVariant = explicitId ? variantsById.get(explicitId) : null;
        if (explicitVariant && String(explicitVariant.handle || "") === handle) return;

        const best = bestVariantForHandle(handle);
        if (!best?.id) return;

        const bestId = String(best.id);
        b.setAttribute("data-offer-v2-variant-id", bestId);

        const href = b.getAttribute("href");
        if (href) b.setAttribute("href", withVariantInHref(href, bestId));

        const shouldDisable = !best.available;
        b.classList.toggle("is-disabled", shouldDisable);
        if (shouldDisable) {
          b.setAttribute("aria-disabled", "true");
          b.setAttribute("tabindex", "-1");
        } else {
          b.removeAttribute("aria-disabled");
          b.removeAttribute("tabindex");
        }
      });

      const currentUrl = new URL(window.location.href);
      const defaultHandle = String(config.productHandle || "");
      const urlVariantId = String(currentUrl.searchParams.get("variant") || "").trim();
      const urlVariantHandle = urlVariantId ? String(variantsById.get(urlVariantId)?.handle || "").trim() : "";
      const urlHandle = handleFromCurrentUrl(currentUrl);
      let chosenHandle = urlVariantHandle || urlHandle || defaultHandle;
      let chosenVariantId = urlVariantId;
      if (!chosenVariantId) {
        chosenVariantId = String(
          boxes.find((b) => b.classList.contains("is-active") && !isPillDisabled(b))?.getAttribute("data-offer-v2-variant-id") || ""
        );
      }

      const firstEnabled = boxes.find((b) => !isPillDisabled(b));
      const hasChosenVariant = chosenVariantId && boxes.some(
        (b) => String(b.getAttribute("data-offer-v2-variant-id") || "") === chosenVariantId
      );
      const hasChosen = hasChosenVariant || boxes.some(
        (b) => String(b.getAttribute("data-offer-v2-handle") || "") === chosenHandle
      );
      if (!hasChosen && firstEnabled) {
        chosenHandle = String(firstEnabled.getAttribute("data-offer-v2-handle") || "");
        chosenVariantId = String(firstEnabled.getAttribute("data-offer-v2-variant-id") || "");
      }

      const currentViewMode = String(currentUrl.searchParams.get("view") || "").toLowerCase();
      const shouldCarryViewMode = currentViewMode === "new-pdp" || currentViewMode === "new-pdp-experiment";

      boxes.forEach((b) => {
        const handle = String(b.getAttribute("data-offer-v2-handle") || "");
        const variantId = String(b.getAttribute("data-offer-v2-variant-id") || "");
        const active = hasChosenVariant
          ? variantId && variantId === chosenVariantId
          : handle && handle === chosenHandle;

        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", active ? "true" : "false");

        // Preserve template view when switching flavors (different product handles).
        const rawHref = b.getAttribute("href");
        if (rawHref && shouldCarryViewMode) {
          try {
            const next = new URL(rawHref, window.location.origin);
            next.searchParams.set("view", currentViewMode);
            b.setAttribute("href", next.pathname + next.search + next.hash);
          } catch (_) {}
        }

        b.addEventListener("click", (e) => {
          if (isPillDisabled(b)) {
            e.preventDefault();
            return;
          }

          if (shouldNavigateOnFlavorClick) {
            return; // allow normal anchor navigation
          }

          e.preventDefault();

          boxes.forEach((x) => {
            x.classList.remove("is-active");
            x.setAttribute("aria-pressed", "false");
          });

          b.classList.add("is-active");
          b.setAttribute("aria-pressed", "true");

          // Keep URL in sync with selected flavor/variant without navigating away.
          const href = b.getAttribute("href");
          if (config.syncFlavorUrl !== false && href && window.history && window.history.replaceState) {
            window.history.replaceState({}, "", href);
          }

          userSelectedPurchaseOption = false;
          rerenderAll();
          hydrateSellingPlanAllocationsIfMissing();

          try {
            window.dispatchEvent(new CustomEvent("new-pdp:flavor-change", {
              detail: { handle, href: b.getAttribute("href") || "" }
            }));
          } catch (_) {}
        });
      });

      setTimeout(() => {
        const first = Array.from(root.querySelectorAll("[data-offer-v2-pill]")).find((p) => !isPillDisabled(p));
        if (first && !root.querySelector("[data-offer-v2-pill].is-active")) {
          first.classList.add("is-active");
          first.setAttribute("aria-pressed", "true");
        }
      }, 0);

      stack.appendChild(frag);
      stack.dataset.offerV2RowsInit = "1";
    }

    function selectedFlavorHandle() {
      const active = root.querySelector("[data-offer-v2-pill].is-active");
      const activeHandle = active?.getAttribute("data-offer-v2-handle");
      if (activeHandle) return activeHandle;

      const defaultHandle = String(config.productHandle || "");
      if (defaultHandle && flavorsByHandle.has(defaultHandle)) return defaultHandle;

      const firstFlavor = resolvedFlavors.find((f) => f && f.handle && f.baseVariantId);
      return firstFlavor ? String(firstFlavor.handle) : null;
    }

    function selectedVariantIds() {
      const handle = selectedFlavorHandle();
      if (!handle) return [];

      const activePill = root.querySelector("[data-offer-v2-pill].is-active");
      const activePillHandle = String(activePill?.getAttribute("data-offer-v2-handle") || "");
      let baseId = "";

      if (!baseId && activePillHandle && activePillHandle === String(handle)) {
        baseId = String(activePill?.getAttribute("data-offer-v2-variant-id") || "") ||
          variantIdFromHref(activePill?.getAttribute("href"));
      }

      const best = bestVariantForHandle(handle);
      if (!baseId && best?.id) baseId = String(best.id);

      if (!baseId) return [];

      if (bagQty <= 1) return [baseId];
      if (bagQty === 2) return [baseId, baseId];
      if (bagQty === 4) return [baseId, baseId, baseId, baseId];

      return [baseId];
    }

    function requestRevliftRefresh() {
      if (!form) return;
      const refresh = () => {
        form.dispatchEvent(new Event("revlift:refresh", { bubbles: true }));
        document.dispatchEvent(new Event("revlift:refresh"));
      };
      refresh();
      window.setTimeout(refresh, 0);
      window.setTimeout(refresh, 120);
      window.setTimeout(refresh, 400);
    }

    function currentRevliftWidget() {
      if (!form) return null;
      return form.querySelector("[data-revlift-subscription-widget]");
    }

    function syncPurchaseOptionSurface(subscriptionState) {
      const useFallback = !subscriptionState?.available;

      if (fallbackPurchaseOptionsEl) {
        fallbackPurchaseOptionsEl.hidden = !useFallback;
        fallbackPurchaseOptionsEl.setAttribute("aria-hidden", useFallback ? "false" : "true");
        if (useFallback) {
          fallbackPurchaseOptionsEl.style.removeProperty("display");
        } else {
          fallbackPurchaseOptionsEl.style.setProperty("display", "none", "important");
        }
      }

      const widget = currentRevliftWidget();
      if (widget) {
        widget.hidden = useFallback;
        widget.setAttribute("aria-hidden", useFallback ? "true" : "false");
        if (useFallback) {
          widget.style.setProperty("display", "none", "important");
        } else {
          widget.style.removeProperty("display");
        }
      }

      if (form) {
        form.toggleAttribute("data-revlift-disable-purchase-options", useFallback);
        form.toggleAttribute("data-revlift-hide-purchase-options", useFallback);
      }

      if (useFallback) {
        purchaseOption = "one_time";
        clearRevliftSellingPlanInputsForOneTime();
      } else {
        requestRevliftRefresh();
      }
    }

    let revliftSurfaceSyncTimer = null;
    function scheduleRevliftSurfaceSync() {
      if (revliftSurfaceSyncTimer) window.clearTimeout(revliftSurfaceSyncTimer);
      revliftSurfaceSyncTimer = window.setTimeout(() => {
        revliftSurfaceSyncTimer = null;
        syncPurchaseOptionSurface(selectedSubscriptionState());
      }, 30);
    }

    if (window.MutationObserver && form) {
      const revliftSurfaceObserver = new MutationObserver((mutations) => {
        const hasWidgetChange = mutations.some((mutation) => {
          const changedNodes = [
            ...Array.from(mutation.addedNodes || []),
            ...Array.from(mutation.removedNodes || []),
          ];

          return changedNodes.some((node) => (
            node.nodeType === 1 &&
            (
              node.matches?.("[data-revlift-subscription-widget]") ||
              node.querySelector?.("[data-revlift-subscription-widget]")
            )
          ));
        });

        if (hasWidgetChange) scheduleRevliftSurfaceSync();
      });

      revliftSurfaceObserver.observe(form, { childList: true, subtree: true });
    }

    function syncRevliftFormLines() {
      if (!form || !revliftLinesEl) return;
      const ids = selectedVariantIds();
      revliftLinesEl.innerHTML = "";

      const counts = new Map();
      ids.forEach((id) => counts.set(String(id), (counts.get(String(id)) || 0) + 1));
      Array.from(counts.entries()).forEach(([id, qty], index) => {
        const idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = `items[${index}][id]`;
        idInput.value = id;
        revliftLinesEl.appendChild(idInput);

        const qtyInput = document.createElement("input");
        qtyInput.type = "hidden";
        qtyInput.name = `items[${index}][quantity]`;
        qtyInput.value = String(academySingleQuantity ? 1 : qty);
        revliftLinesEl.appendChild(qtyInput);
      });

      // The app prices the submitted variant lines using the same per-unit
      // rounding as cart. Publishing aggregate theme totals overrides that quote.

      if (primaryQuantityInput) {
        primaryQuantityInput.value = String(academySingleQuantity ? 1 : Math.max(1, ids.length));
        primaryQuantityInput.setAttribute("value", primaryQuantityInput.value);
      }

      if (primaryVariantInput && ids[0]) {
        const nextId = String(ids[0]);
        primaryVariantInput.value = nextId;
        primaryVariantInput.setAttribute("value", nextId);
        primaryVariantInput.dispatchEvent(new Event("input", { bubbles: true }));
        primaryVariantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      requestRevliftRefresh();
    }

    function syncPurchaseOptionFromRevlift() {
      if (!form) return;
      const checked = form.querySelector(
        'input[name="revlift_selling_plan_choice"]:checked, input[name="revlift_landing_selling_plan_choice"]:checked'
      );
      if (checked) {
        purchaseOption = checked.value === "subscription" ? "subscribe" : "one_time";
        return;
      }
      const singleSellingPlan = form.querySelector('input[name="selling_plan"]');
      if (singleSellingPlan && String(singleSellingPlan.value || "").trim()) {
        purchaseOption = "subscribe";
      }
    }

    function clearRevliftSellingPlanInputsForOneTime() {
      if (!form) return;
      form
        .querySelectorAll('input[name="selling_plan"], input[name^="items["][name$="[selling_plan]"]')
        .forEach((input) => input.remove());
    }

    function revliftSellingPlanByVariantId() {
      const plans = new Map();
      if (!form) return plans;
      let data = null;
      try { data = new FormData(form); } catch { return plans; }
      const idsByIndex = new Map();
      for (const [name, value] of data.entries()) {
        const idMatch = String(name).match(/^items\[(\d+)\]\[id\]$/);
        if (idMatch) idsByIndex.set(idMatch[1], String(value || ""));
      }
      for (const [name, value] of data.entries()) {
        const planMatch = String(name).match(/^items\[(\d+)\]\[selling_plan\]$/);
        if (!planMatch || !value) continue;
        const id = idsByIndex.get(planMatch[1]);
        if (id) plans.set(id, Number(value));
      }
      const singlePlan = Number(form.querySelector('input[name="selling_plan"]')?.value || 0);
      if (singlePlan > 0) {
        idsByIndex.forEach((id) => plans.set(String(id), singlePlan));
      }
      return plans;
    }





    function anySoldOut(ids) {
      return ids.some((id) => {
        const v = variantsById.get(String(id));
        return v ? !v.available : true;
      });
    }

    function totalCents(ids) {
      let t = 0;
      ids.forEach((id) => {
        const v = variantsById.get(String(id));
        if (v) t += Number(v.price || 0);
      });
      return t;
    }

    function discounted(total, pct) {
      // Shopify truncates the percentage discount in cents for each unit.
      return total - Math.floor(total * pct / 100);
    }

    function setPurchaseOption(val, options = {}) {
      if (options.userInitiated === true) userSelectedPurchaseOption = true;

      const requestedOption = (val === "subscribe") ? "subscribe" : "one_time";
      const subscriptionState = selectedSubscriptionState();
      purchaseOption = requestedOption === "subscribe" && !subscriptionState.available ? "one_time" : requestedOption;
      setSubscribeOptionAvailable(subscriptionState.available);
      syncPurchaseOptionSurface(subscriptionState);

      poCards.forEach((card) => {
        const option = card.getAttribute("data-offer-v2-po");
        const isSubscribe = option === "subscribe";
        const isActive = option === purchaseOption;
        if (!isSubscribe) {
          card.hidden = false;
          card.setAttribute("aria-hidden", "false");
        }
        card.classList.toggle("is-active", isActive);
      });

      poRadios.forEach((r) => {
        const isSubscribe = r.value === "subscribe";
        r.checked = (r.value === purchaseOption);
        r.disabled = isSubscribe && !subscriptionState.available;
      });

      if (purchaseOption === "subscribe") {
        maybeLoadPlansForHandle(selectedFlavorHandle());
      }
      renderPlanOptions();
      rerenderAll();
    }

    function renderPurchaseOptions() {
      const ids = selectedVariantIds();
      if (!ids.length) return;
      const subscriptionState = selectedSubscriptionState(ids);
      syncPurchaseOptionSurface(subscriptionState);

      if (
        purchaseOption !== "subscribe" &&
        defaultPurchaseOption === "subscribe" &&
        subscriptionState.available &&
        !userSelectedPurchaseOption
      ) {
        setPurchaseOption("subscribe");
        return;
      }

      if (purchaseOption === "subscribe" && !subscriptionState.available) {
        setPurchaseOption("one_time");
        return;
      }

      setSubscribeOptionAvailable(subscriptionState.available);

      const base = totalCents(ids);


      const qtyPct = effectiveQtySavePctFor(bagQty);
      const subscriptionQtyPct = effectiveQtySavePctFor(bagQty, "subscription");

      const onePrice = ids.reduce((sum, id) =>
        sum + discounted(Number(variantsById.get(String(id))?.price || 0), qtyPct), 0);

      // Apply the actual selling-plan allocation before the per-unit offer discount.
      const subPrice = ids.reduce((sum, id) => {
        const variant = variantsById.get(String(id));
        const unit = Number(variant?.price || 0);
        const handleForVariant = String(variant?.handle || selectedFlavorHandle() || '');
        const selectedPlanId = subscriptionState.planId || getSelectedPlanIdForHandle(handleForVariant) || Number(variant?.spId || 0) || null;
        const alloc = subscriptionState.allocations.get(String(id)) || getPlanAlloc(id, selectedPlanId) || spAllocById.get(String(id));
        const planPct = planPctFromAlloc(alloc, unit);
        const planPrice = alloc?.price != null
          ? Number(alloc.price)
          : (planPct != null ? Math.round(unit * (100 - planPct) / 100) : unit);
        const unitFinal = discounted(planPrice, subscriptionQtyPct);
        return sum + unitFinal;
      }, 0);


      const onePriceEl = $(root, '[data-offer-v2-po-price="one_time"]');
      const oneCompareEl = $(root, '[data-offer-v2-po-compare="one_time"]');
      const subPriceEl = $(root, '[data-offer-v2-po-price="subscribe"]');
      const subCompareEl = $(root, '[data-offer-v2-po-compare="subscribe"]');

      if (onePriceEl) onePriceEl.textContent = fmtMoney(fmt, onePrice);
      if (oneCompareEl) {
        oneCompareEl.textContent = fmtMoney(fmt, base);
        oneCompareEl.hidden = !(base > onePrice);
      }

      if (subPriceEl) subPriceEl.textContent = fmtMoney(fmt, subPrice);
      if (subCompareEl) {
        subCompareEl.textContent = fmtMoney(fmt, base);
        subCompareEl.hidden = !(base > subPrice);
      }

      // --- Badge numbers ---
      // One-time badge: match qty card percent, hide when no savings.
      if (oneBadgeEl) {
        oneBadgeEl.hidden = qtyPct <= 0;
        if (qtyPct > 0) oneBadgeEl.textContent = replacePctInTemplate(oneBadgeTemplate, qtyPct);

        const oneCard = oneBadgeEl.closest('[data-offer-v2-po="one_time"]');
        if (oneCard) {
          oneCard.classList.toggle("offer-cta-v2__poCard--no-badge", !!oneBadgeEl.hidden);
        }
      }

      // Subscribe badge: total effective percent off base after BOTH discounts
      let combinedPct = 0;
      if (base > 0) combinedPct = clamp(Math.round((1 - subPrice / base) * 100), 0, 95);
      if (subBadgeEl) subBadgeEl.textContent = replacePctInTemplate(subBadgeTemplate, combinedPct);

      // per-unit (3 decimals), computed in dollars for fractional cents
      const selectedHandle = selectedFlavorHandle();
      const treatInfo = getTreatTotalsForHandle(selectedHandle);
      const treatTotalsForHandle = treatInfo.totals;
      const hasTreatTotalsForHandle = treatInfo.hasTreatTotalsForHandle;

      const usePerBagFallback = !hasTreatTotalsForHandle;
      const denom = usePerBagFallback
        ? Math.max(1, bagQty)
        : (treatTotalsForHandle.get(bagQty) || treatsTotalByQty.get(bagQty) || Math.max(1, treatsPerBag * Math.max(1, bagQty)));
      const unitLabel = usePerBagFallback ? "bag" : qtyUnitSingular;
      const onePTDollars = (onePrice / 100) / denom;
      const subPTDollars = (subPrice / 100) / denom;

      const onePTEl = $(root, '[data-offer-v2-po-per-treat="one_time"]');
      const subPTEl = $(root, '[data-offer-v2-po-per-treat="subscribe"]');

      if (onePTEl) onePTEl.textContent = `~${fmtMoneyDollars(fmt3, onePTDollars)}/${unitLabel}`;
      if (subPTEl) subPTEl.textContent = `~${fmtMoneyDollars(fmt3, subPTDollars)}/${unitLabel}`;

    }

    function renderSoldOut() {
      const ids = selectedVariantIds();
      const bad = ids.length === 0 ? true : anySoldOut(ids);

      if (helpEl) helpEl.hidden = !bad;
      if (atcBtn) atcBtn.disabled = bad;
    }

    function getTitleSuffixForHandle(handle) {
      const key = String(handle || "").trim();
      if (key && Object.prototype.hasOwnProperty.call(titleSuffixByHandle, key)) {
        return String(titleSuffixByHandle[key] || "").trim();
      }
      if (key && key !== String(config.productHandle || "").trim()) return "";
      return titleSuffix || "";
    }

    function withTitleSuffix(baseTitle, handle) {
      const title = String(baseTitle || "").trim();
      if (!title) return "";
      const suffixValue = getTitleSuffixForHandle(handle);
      if (!suffixValue) return title;
      const suffix = ` - ${suffixValue}`;
      return title.endsWith(suffix) ? title : `${title}${suffix}`;
    }

    function toOrdinal(day) {
      const mod10 = day % 10;
      const mod100 = day % 100;
      if (mod10 === 1 && mod100 !== 11) return `${day}st`;
      if (mod10 === 2 && mod100 !== 12) return `${day}nd`;
      if (mod10 === 3 && mod100 !== 13) return `${day}rd`;
      return `${day}th`;
    }

    function addCalendarDays(date, daysToAdd) {
      const d = new Date(date);
      d.setDate(d.getDate() + Number(daysToAdd || 0));
      return d;
    }

    function formatDeliveryDate(date) {
      const month = date.toLocaleString(undefined, { month: "long" });
      return `${month} ${toOrdinal(date.getDate())}`;
    }

    function formatDeliveryDateRange(now = new Date()) {
      const maxDate = addCalendarDays(now, 5);
      return formatDeliveryDate(maxDate);
    }

    function renderDeliveryTimeline() {
      if (!deliveryEl || !deliveryDateEl) return;
      placeDeliveryBeforeAtc();
      const ids = selectedVariantIds();
      const inStock = ids.length > 0 && !anySoldOut(ids);
      deliveryEl.hidden = !inStock;
      if (!inStock) return;

      deliveryDateEl.textContent = formatDeliveryDateRange();
    }

    function extractReviewCountTokenFromText(rawText) {
      const text = String(rawText || "").replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
      if (!text) return "";

      const tokenMatch =
        text.match(/(\d[\d,.]*\s*[kKmM]\+?)/) ||
        text.match(/(\d[\d,.]*\+?)/);
      return tokenMatch?.[1] ? tokenMatch[1].replace(/\s+/g, "") : "";
    }

    function ratingsSuffixText() {
      const token = extractReviewCountTokenFromText(defaultRatingsReviewsText);
      if (!token) return " Reviews";

      const suffix = defaultRatingsReviewsText.replace(token, "");
      return suffix.trim() ? suffix : " Reviews";
    }

    function normalizeReviewCountFromConfig(rawValue) {
      if (rawValue == null || rawValue === "") return "";

      if (typeof rawValue === "number" && Number.isFinite(rawValue) && rawValue > 0) {
        return Math.round(rawValue).toLocaleString();
      }

      const rawText = String(rawValue).trim();
      if (!rawText) return "";

      const asNumber = Number(rawText.replace(/,/g, ""));
      if (Number.isFinite(asNumber) && asNumber > 0) {
        return Math.round(asNumber).toLocaleString();
      }

      return extractReviewCountTokenFromText(rawText);
    }

    function getConfiguredReviewCountToken(handle) {
      const key = String(handle || "").trim();
      if (!key) return "";
      if (!Object.prototype.hasOwnProperty.call(reviewCountByHandle, key)) return "";
      return normalizeReviewCountFromConfig(reviewCountByHandle[key]);
    }

    function getJudgeMeReviewCountToken() {
      const widgetRoots = Array.from(document.querySelectorAll(".jdgm-rev-widg"));
      const primarySelectors = [
        ".jdgm-histogram__review-count",
        ".jdgm-rev-widg__summary-text",
        ".jdgm-rev-widg__reviews-count",
      ];

      for (const widget of widgetRoots) {
        for (const selector of primarySelectors) {
          const nodes = Array.from(widget.querySelectorAll(selector));
          for (const node of nodes) {
            const token = extractReviewCountTokenFromText(node.textContent);
            if (token) return token;
          }
        }
      }

      const previewNodes = Array.from(document.querySelectorAll(".jdgm-prev-badge__text"));
      for (const node of previewNodes) {
        const token = extractReviewCountTokenFromText(node.textContent);
        if (token) return token;
      }

      return "";
    }

    function syncTopReviewCountFromWidget() {
      if (!ratingsReviewsEl) return false;

      const countToken =
        getConfiguredReviewCountToken(selectedFlavorHandle()) ||
        getJudgeMeReviewCountToken();
      if (!countToken) return false;

      if (ratingsNumEl) {
        if (ratingsNumEl.textContent !== countToken) ratingsNumEl.textContent = countToken;
        return true;
      }

      const suffix = ratingsSuffixText();
      const spacer = suffix.startsWith(" ") ? "" : " ";
      const nextText = `${countToken}${spacer}${suffix}`;
      if (ratingsReviewsEl.textContent !== nextText) ratingsReviewsEl.textContent = nextText;
      return true;
    }

    function scheduleTopReviewCountSync() {
      if (!ratingsReviewsEl) return;
      if (reviewSyncRaf) cancelAnimationFrame(reviewSyncRaf);

      reviewSyncRaf = requestAnimationFrame(() => {
        reviewSyncRaf = 0;
        const synced = syncTopReviewCountFromWidget();
        if (!synced && reviewSyncAttempts < 15) {
          reviewSyncAttempts += 1;
          window.setTimeout(scheduleTopReviewCountSync, 400);
        }
      });
    }

    function renderSelectedFlavorTitle() {
      if (!titleEl) return;

      const handle = selectedFlavorHandle();
      let nextTitle = "";

      if (handle) {
        if (Object.prototype.hasOwnProperty.call(productTitleByHandle, String(handle))) {
          nextTitle = String(productTitleByHandle[String(handle)] || "").trim();
        }

        const best = bestVariantForHandle(handle);
        if (!nextTitle) nextTitle = String(best?.title || "").trim();

        if (!nextTitle) {
          const firstForHandle = variants.find((v) =>
            String(v?.handle || "") === String(handle) && String(v?.title || "").trim().length > 0
          );
          nextTitle = String(firstForHandle?.title || "").trim();
        }

        if (!nextTitle) {
          const flavor = flavorsByHandle.get(String(handle));
          nextTitle = String(flavor?.title || "").trim();
        }
      }

      if (!nextTitle) nextTitle = defaultTitleText;
      if (nextTitle) titleEl.textContent = withTitleSuffix(nextTitle, handle);
    }

    function rerenderAll() {
      const activeHandle = selectedFlavorHandle();
      maybeLoadPlansForHandle(activeHandle);
      renderPlanOptions();
      syncGalleryToHandle(activeHandle);
      renderSelectedFlavorTitle();
      renderQtyCardBadges();
      renderQtyCardTreatCounts();
      renderPurchaseOptions();
      renderSoldOut();
      renderDeliveryTimeline();
      scheduleTopReviewCountSync();
      fitQtyPerkText();
      syncRevliftFormLines();
    }

    document.addEventListener("revlift:storewide-state", rerenderAll);

    if (window.MutationObserver && form && deliveryEl && atcBtn) {
      const deliveryPlacementObserver = new MutationObserver(placeDeliveryBeforeAtc);
      deliveryPlacementObserver.observe(form, { childList: true, subtree: true });
      window.setTimeout(placeDeliveryBeforeAtc, 0);
      window.setTimeout(placeDeliveryBeforeAtc, 250);
      window.setTimeout(placeDeliveryBeforeAtc, 1000);
    }

    function setQty(q) {
      if (academySingleQuantity) {
        bagQty = 1;
        ensureRows();
        rerenderAll();
        return;
      }

      const parsedQty = normalizeBagQty(q || 1);
      bagQty = validBagQty.has(parsedQty) ? parsedQty : 1;

      qtyCards.forEach((btn) => {
        const active = normalizeBagQty(btn.getAttribute("data-offer-v2-qty") || 0) === bagQty;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });

      ensureRows();
      rerenderAll();
    }

    qtyCards.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        setQty(btn.getAttribute("data-offer-v2-qty"));
      });
    });

    poRadios.forEach((r) => r.addEventListener("change", () => setPurchaseOption(r.value, { userInitiated: true })));
    poCards.forEach((card) => card.addEventListener("click", () => {
      setPurchaseOption(card.getAttribute("data-offer-v2-po"), { userInitiated: true });
    }));
    if (planOptionsWrap) {
      planOptionsWrap.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-offer-v2-plan-key]");
        if (!btn || !planOptionsWrap.contains(btn)) return;

        e.preventDefault();
        setPurchaseOption("subscribe", { userInitiated: true });

        const nextKey = String(btn.getAttribute("data-offer-v2-plan-key") || "").trim();
        if (!nextKey || nextKey === selectedPlanKey) return;

        selectedPlanKey = nextKey;
        renderPlanOptions();
        rerenderAll();
      });
    }

    async function addItems(items) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
  const data = await res.json().catch(() => null);
  console.error("Cart error:", data);
  throw new Error(data?.description || "Cart add failed");
}

      return res.json();
    }

    function openCartDrawerOrRedirect() {
      const bubble = document.querySelector("#cart-icon-bubble");
      if (bubble) {
        bubble.click();        // lets the theme/AMP open + refresh correctly
        return;
      }
      window.location.href = "/cart";
    }

    function dispatchCartEvents() {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:updated"));
      window.dispatchEvent(new CustomEvent("cart:refresh"));
      window.dispatchEvent(new CustomEvent("cart:updated"));
    }

    form?.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!atcBtn || atcBtn.disabled) return;

  syncRevliftFormLines();
  syncPurchaseOptionFromRevlift();
  if (purchaseOption !== "subscribe") clearRevliftSellingPlanInputsForOneTime();
  const ids = selectedVariantIds();
  if (!ids.length) return;

  atcBtn.disabled = true;
  atcBtn.classList.add("is-loading");

  try {
    if (purchaseOption === "subscribe") {
      const activeHandle = selectedFlavorHandle();
      if (activeHandle && !planAllocHydratedByHandle.has(String(activeHandle))) {
        await loadPlansForHandle(activeHandle);
        rerenderAll();
      }
    }

    const counts = new Map();
    ids.forEach((id) => counts.set(String(id), (counts.get(String(id)) || 0) + 1));
    const subscriptionState = purchaseOption === "subscribe"
      ? selectedSubscriptionState(ids)
      : { available: false, planId: null, allocations: new Map() };
    const revliftPlanByVariantId = purchaseOption === "subscribe" ? revliftSellingPlanByVariantId() : new Map();

    if (purchaseOption === "subscribe" && !subscriptionState.available && !revliftPlanByVariantId.size) {
      console.warn("Subscribe selected but one or more variants have no selling plan allocation", { ids });
      alert("Subscription is unavailable for this selection.");
      return;
    }

    const items = Array.from(counts.entries()).map(([id, qty]) => {
      const safeQty = academySingleQuantity ? 1 : qty;
      const item = { id: Number(id), quantity: safeQty };
      if (purchaseOption === "subscribe") {
        const variant = variantsById.get(String(id));
        const planId = revliftPlanByVariantId.get(String(id)) || subscriptionState.planId || getSelectedPlanIdForHandle(String(variant?.handle || selectedFlavorHandle() || "")) || Number(variant?.spId || 0);
        if (planId > 0 && subscriptionState.allocations.has(String(id))) item.selling_plan = planId;
        if (planId > 0 && revliftPlanByVariantId.has(String(id))) item.selling_plan = planId;
      }
      return item;
    });

    if (purchaseOption === "subscribe") {
      const hasMissingPlan = items.some((item) => item.selling_plan == null);
      if (hasMissingPlan) {
        console.warn("Subscribe selected but one or more variants have no selling plan", { ids, items });
        alert("Subscription is unavailable for this selection.");
        return;
      }
    }

    await addItems(items);

    window.__pupfordCartDrawerNeedsRefreshUntil = Date.now() + 5000;
    dispatchCartEvents();
    openCartDrawerOrRedirect();

  } catch (err) {
    console.error(err);
    alert("Sorry — we couldn’t add that to cart. Please try again.");
  } finally {
    setTimeout(() => {
      atcBtn.disabled = false;
      atcBtn.classList.remove("is-loading");
    }, 300);
  }
});



    // Refit on resize
    const onResize = () => fitQtyPerkText();
    window.addEventListener("resize", onResize);

    scheduleTopReviewCountSync();

    if (qtyCardsWrap && window.ResizeObserver) {
      const ro = new ResizeObserver(() => fitQtyPerkText());
      ro.observe(qtyCardsWrap);
      root.__offerCTAv2RO = ro;
    }

    // init
    (async () => {
      renderQtyCardPerks();
      await bootstrapFromRuntimeIfEmpty();
      setQty(defaultBagQtyRaw || 1);
      setPurchaseOption(defaultPurchaseOption);
      setTimeout(fitQtyPerkText, 50);
      hydrateSellingPlanAllocationsIfMissing();
    })();
  }

  function collectInitRoots(scope) {
    const roots = new Set();
    const queryRoot = scope?.querySelectorAll?.bind(scope);
    if (!queryRoot) return [];

    queryRoot("[data-new-pdp-cta]").forEach((root) => roots.add(root));

    queryRoot("[data-offer-v2-config]").forEach((cfgEl) => {
      const cfg = safeJson(cfgEl.textContent, null);
      const isNewPdpConfig =
        !!cfg &&
        (
          cfg.newPdpCta === true ||
          (
            typeof cfg.productHandle === "string" &&
            cfg.productHandle.trim().length > 0 &&
            Array.isArray(cfg.variants)
          ) ||
          Array.isArray(cfg.flavors) ||
          Array.isArray(cfg.groupProductHandles) ||
          Object.prototype.hasOwnProperty.call(cfg, "flavorNavigationMode")
        );
      if (!isNewPdpConfig) return;

      const root =
        cfgEl.closest("[data-new-pdp-cta], [data-offer-cta]") ||
        cfgEl.closest("section.offer-cta") ||
        cfgEl.closest("#offer-cta");
      if (root) roots.add(root);
    });

    return Array.from(roots);
  }

  document.addEventListener("DOMContentLoaded", () => {
    collectInitRoots(document).forEach(initOfferCTAv2);
  });

  document.addEventListener("shopify:section:load", (e) => {
    collectInitRoots(e.target).forEach(initOfferCTAv2);
  });
})();

document.addEventListener("click", (e) => {
  const pill = e.target.closest("[data-offer-v2-pill]");
  if (!pill) return;


  const handle = pill.getAttribute("data-offer-v2-handle");
  if (!handle) return;

  const root =
    pill.closest("[data-new-pdp-cta], [data-offer-cta]") ||
    pill.closest("section.offer-cta") ||
    document;
  const galleriesWrap = root.querySelector("[data-offer-v2-galleries]");
  if (!galleriesWrap) return;

  const slots = galleriesWrap.querySelectorAll("[data-offer-v2-gallery]");
  slots.forEach((slot) => {
    const isMatch = slot.getAttribute("data-offer-v2-handle") === handle;
    slot.toggleAttribute("hidden", !isMatch);
    slot.classList.toggle("is-active", isMatch);
  });

  // If your gallery snippet initializes a slider (Swiper/Flickity/etc),
  // you may need to "update" it here after un-hiding.
});
